import differenceBy from 'lodash/differenceBy';
import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton, notification, useMe } from 'src/common';
import styles from './index.module.scss';
import { IMember, AccessControlPolicy } from 'src/models/acl/types';
import AccessControlItem, { Mode } from 'src/common/AccessControlItem';
import { useEmployees } from 'src/models/users/hooks';
import { IInbox } from 'src/models/callOffs/types';
import { useUpdateInbox } from 'src/models/callOffs/hooks';
import { toMembersList, getPreparedMembersArray } from 'src/models/acl/helpers';
import {
  trackUpdateInboxOwnersPolicy,
  trackUpdateInboxSubscribersPolicy,
  trackUpdateInboxSubscribersAndOwners,
  trackUpdateInboxSubscribers,
  trackUpdateInboxOwners
} from 'src/segment/events';

interface ICompanyTypeProps {
  currentInbox: IInbox;
  readOnly: boolean;
}

export const AccessControlList: FC<ICompanyTypeProps> = props => {
  const { currentInbox, readOnly } = props;

  const inboxId = currentInbox.emailAddress;
  const { t } = useTranslation();
  const [updateInbox] = useUpdateInbox();

  const { data: meData } = useMe();

  const myUserId = meData?.email;

  const { data: employees, loading: employeeLoading } = useEmployees();

  const owner = employees?.createdBy;

  const subscribers = useMemo((): IMember[] => {
    if (employees && owner) {
      return toMembersList(currentInbox.subscribers || [], owner);
    } else {
      return []; // we can not show subscribers while we do not know who is an owner
    }
  }, [currentInbox.subscribers, employees, owner]);
  const owners = useMemo((): IMember[] => {
    if (employees && owner) {
      return toMembersList(currentInbox.owners || [], owner);
    } else {
      return []; // we can not show subscribers while we do not know who is an owner
    }
  }, [currentInbox.owners, employees, owner]);

  const subscribePolicy = currentInbox.accessControl.subscribePolicy;
  const editPolicy = currentInbox.accessControl.editPolicy;

  const updateWSOwnersList = useCallback(
    (owners: string[]) => {
      updateInbox({ variables: { inboxId, owners } })
        .then(() => {
          trackUpdateInboxOwners(inboxId);
        })
        .catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
    },
    [inboxId, t, updateInbox]
  );

  const updateWSSubscribersList = useCallback(
    (subscribers: string[]) => {
      updateInbox({ variables: { inboxId, subscribers } })
        .then(() => {
          trackUpdateInboxSubscribers(inboxId);
        })
        .catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
    },
    [inboxId, t, updateInbox]
  );

  const onMoveMember = useCallback(
    (email: string, isOwner?: boolean) => {
      const ownersIds = owners.map(({ user }) => user.email);
      const subscribersIds = subscribers.map(({ user }) => user.email);

      const ownersVal = getPreparedMembersArray(ownersIds, email, isOwner);
      const subscribersVal = getPreparedMembersArray(subscribersIds, email, !isOwner);

      updateInbox({ variables: { inboxId, owners: ownersVal, subscribers: subscribersVal } })
        .then(() => {
          trackUpdateInboxSubscribersAndOwners(inboxId);
        })
        .catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
    },
    [owners, subscribers, updateInbox, inboxId, t]
  );

  const changeWSOwnerPolicy = useCallback(
    (policy: AccessControlPolicy) => {
      updateInbox({ variables: { inboxId, editPolicy: policy } })
        .then(() => {
          trackUpdateInboxOwnersPolicy(inboxId, policy);
        })
        .catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
    },
    [inboxId, t, updateInbox]
  );

  const changeWSPolicy = useCallback(
    (policy: AccessControlPolicy) => {
      updateInbox({ variables: { inboxId, subscribePolicy: policy } })
        .then(() => {
          trackUpdateInboxSubscribersPolicy(inboxId, policy);
        })
        .catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
    },
    [inboxId, t, updateInbox]
  );

  // FIXME: @ acl owners/subs fns pretty much same for mp, inbox, ws -- would be good to unify it and reuse
  // SHOW ONLY MEMBERS WHO ARE NOT OWNER OR SUBSCRIBER  YET
  const membersOptions = useMemo((): IMember[] => {
    const employeesList =
      employees && owner ? toMembersList([owner, ...employees.personel.filter(emp => emp.enabled)], owner) : [];

    return differenceBy(employeesList, [...owners, ...subscribers], 'user.email');
  }, [employees, owner, owners, subscribers]);

  const isLoading = employeeLoading;

  return isLoading ? (
    <Skeleton active={isLoading} />
  ) : (
    <div className={styles.container}>
      <AccessControlItem
        myUserId={myUserId}
        readOnly={readOnly}
        mode={Mode.Owners}
        membersOptions={membersOptions}
        members={owners}
        onUpdate={updateWSOwnersList}
        onMove={onMoveMember}
        description={t('CallOffs.infoAboutInboxOwners')}
        policy={editPolicy}
        onChangePolicy={changeWSOwnerPolicy}
        policyDescription={t('Common.ACL.setEverybodyAsEditor')}
      />
      <AccessControlItem
        myUserId={myUserId}
        readOnly={readOnly}
        mode={Mode.Subscribers}
        membersOptions={membersOptions}
        members={subscribers}
        onUpdate={updateWSSubscribersList}
        onMove={onMoveMember}
        policy={subscribePolicy}
        onChangePolicy={changeWSPolicy}
        policyDescription={t('CallOffs.giveAllTeammatesPermissionToInbox')}
        description={t('CallOffs.infoAboutInboxSubscribers')}
      />
    </div>
  );
};

export default AccessControlList;
